import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import stripeService from '../../services/stripeService'; // Import stripe service

// Import Material UI components
import {
  Box, Typography, Paper, Button, Divider, Alert, CircularProgress,
  Stepper, Step, StepLabel, Card, CardContent,
  TextField, Grid, Container
} from '@mui/material';

// Import Material UI icons
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Lock as LockIcon
} from '@mui/icons-material';

const steps = ['Review', 'Payment Details', 'Confirmation'];

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const SubscriptionCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  
  // Payment form state
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvc: ''
  });

  useEffect(() => {
    const loadPlanDetails = async () => {
      try {
        setLoading(true);
        
        // Get plan ID from location state
        const planId = location.state?.planId;
        
        if (!planId) {
          setError("No plan selected. Please choose a subscription plan.");
          setLoading(false);
          return;
        }

        try {
          // Fetch plans using the admin-specific endpoint
          const plansResponse = await stripeService.getAdminViewablePlans();
          
          if (plansResponse && plansResponse.success && Array.isArray(plansResponse.data)) {
            const selectedPlan = plansResponse.data.find(p => p.id === planId);
            if (selectedPlan) {
              setPlan(selectedPlan);
              setLoading(false);
              return;
            }
          }
          
          throw new Error("Selected plan not found");
        } catch (apiErr) {
          console.error("Could not fetch plan from API:", apiErr);
          // Fall back to location state data if provided
          if (location.state?.planName && location.state?.price) {
            setPlan({
              id: planId,
              name: location.state.planName,
              price: location.state.price,
              interval: location.state.interval || 'month',
              description: location.state.description || `${location.state.planName} Plan`,
              features: location.state.features || [
                'Full access to all features',
                'Technical support',
                'Regular updates'
              ]
            });
            setLoading(false);
            return;
          }
          
          // If we can't get the plan details, show error
          setError("Could not retrieve plan details. Please try again.");
        }
      } catch (err) {
        console.error("Failed to load plan details:", err);
        setError("Failed to load subscription information. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadPlanDetails();
  }, [location.state]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const redirectToStripeCheckout = async () => {
    try {
      setProcessingCheckout(true);
      setError(null);
      
      if (!plan || !plan.id) {
        throw new Error("No plan selected");
      }
      
      // Define success and cancel URLs
      const successUrl = `${window.location.origin}/admin/subscription/success`;
      const cancelUrl = `${window.location.origin}/admin/subscription/cancel`;
      
      console.log(`Creating checkout session for plan: ${plan.id}`);
      console.log(`Success URL: ${successUrl}`);
      console.log(`Cancel URL: ${cancelUrl}`);
      
      // Create checkout session using the stripe service
      const response = await stripeService.createCheckoutSession(
        plan.id,
        successUrl,
        cancelUrl
      );
      
      console.log('Checkout session response:', response);
      
      // Handle all possible response formats
      let checkoutUrl = null;
      
      if (response?.data?.url) {
        // Format 1: { data: { url: "..." } }
        checkoutUrl = response.data.url;
      } else if (response?.data?.checkoutUrl) {
        // Format 2: { data: { checkoutUrl: "..." } }
        checkoutUrl = response.data.checkoutUrl;
      } else if (response?.checkoutUrl) {
        // Format 3: { checkoutUrl: "..." }
        checkoutUrl = response.checkoutUrl;
      } else if (response?.url) {
        // Format 4: { url: "..." }
        checkoutUrl = response.url;
      } else if (typeof response === 'string' && response.startsWith('http')) {
        // Format 5: direct URL string
        checkoutUrl = response;
      }
      
      if (checkoutUrl) {
        console.log(`Redirecting to checkout URL: ${checkoutUrl}`);
        window.location.href = checkoutUrl;
      } else {
        console.error("Invalid checkout session response format:", response);
        throw new Error("The server returned an invalid response format. Please try again.");
      }
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      
      // Detailed error logging for debugging
      if (err.response) {
        console.error('Error response status:', err.response.status);
        console.error('Error response data:', err.response.data);
        
        // Use backend error message if available
        const errorMessage = err.response.data?.message || err.response.data?.error || err.message;
        setError(`Payment processing failed: ${errorMessage}. Please try again.`);
      } else if (err.request) {
        // Request was made but no response received
        console.error('No response received:', err.request);
        setError("No response received from the server. Please check your network connection and try again.");
      } else {
        // Something else caused the error
        setError(`Payment processing failed: ${err.message}. Please try again.`);
      }
      
      setProcessingCheckout(false);
    }
  };
  
  const handleNext = () => {
    if (activeStep === 0) {
      // In a real implementation, we'll redirect to Stripe checkout directly
      // instead of showing our own payment form
      redirectToStripeCheckout();
    } else if (activeStep === 1) {
      // This step is not used with Stripe Checkout, but kept for future flexibility
      setActiveStep(2);
    } else {
      // Complete the process
      navigate('/admin/dashboard');
    }
  };
  
  const handleBack = () => {
    if (activeStep === 0) {
      // Go back to subscription plans
      navigate('/subscription');
    } else {
      setActiveStep(prevStep => prevStep - 1);
    }
  };
  
  if (loading) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, md: 4 }, 
          mb: 4,
          px: { xs: 2, md: 3 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  const ReviewStep = () => (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Review Your Subscription
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Please review your selected plan details before proceeding to payment.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Plan:
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography variant="subtitle1" fontWeight="medium">
              {plan.name}
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Price:
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography variant="subtitle1" fontWeight="medium">
              {formatCurrency(plan.price)}/{plan.interval}
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Billing:
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography variant="subtitle1">
              {plan.interval === 'year' ? 'Annual' : 'Monthly'} recurring payment
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Included Features:</Typography>
        
        <Box sx={{ pl: 2 }}>
          {plan.features && Array.isArray(plan.features) ? (
            plan.features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Feature list not available
            </Typography>
          )}
        </Box>
        
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
          <LockIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">
            You'll be redirected to Stripe's secure checkout page to complete your payment.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
  
  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <CreditCardIcon sx={{ mr: 1 }} /> Complete Your Subscription
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          You're just a few steps away from accessing all the premium features
        </Typography>
      </Paper>

      {/* Error message */}
      {error && (
        <Alert 
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <ReviewStep />

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Back to Plans
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={processingCheckout}
          sx={{
            minWidth: '180px',
            boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
          }}
        >
          {processingCheckout ? (
            <>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            <>Proceed to Checkout</>
          )}
        </Button>
      </Box>
    </Container>
  );
};

export default SubscriptionCheckout;
